import { colorBands } from "src/organization/pages/project/environment/metrics/constant";
import { defaultLayoutDimensions } from "src/organization/pages/project/environment/metrics/http/config";
import useUpdateDimension from "src/organization/pages/project/environment/metrics/http/hook/useUpdateDimension";

import { TotalFilterType } from "./TotalFilter";
import {
  MiniMetrics,
  SeriesDataType,
  ServiceMetricsType,
  MetricsUnit,
  resouceUnit,
  MetricsUnit2
} from "./type";

const dimension = { ...defaultLayoutDimensions, height: 240 };
export const useUpdateTotalDimension = (id: string) => {
  return useUpdateDimension(id, dimension);
};

const getInPointInstance = (
  point?: MiniMetrics,
  serviceName?: string,
  unit?: MetricsUnit
) => {
  if (!point) {
    return { isMultiInstace: false, value: 0, instances: [], max: 0 };
  }

  const instancePattern = new RegExp(`^${serviceName}\\.\\d+\\@value$`);
  const instances = Object.keys(point).filter(p => instancePattern.test(p));

  // use average for single instance and sum all instances in multi case
  const isMultiInstace = instances.length > 1;
  const value = isMultiInstace
    ? instances.reduce((a, n) => {
        a += point[n];
        return a;
      }, 0)
    : point?.["average@value"] || 0;

  //Network storages have just single instance no need for the multiplier
  const max =
    unit && ["/mnt", "/tmp"].includes(unit)
      ? point.max
      : point.max * instances.length;
  return { isMultiInstace, value, instances, max };
};
/**
 * group metrics by service kind name and add additional metricsMap that converts
 * metrics entry into a Map container for quick retrival
 */
const groupByKindName = (serviceMetrics?: ServiceMetricsType[]) => {
  type GroupType = Record<ServiceMetricsType["kind"], ServiceMetricsType[]>;
  const dateSet = new Set<string>();
  const servicekind = serviceMetrics?.reduce((accum, service) => {
    //convert each metrics into a map for faster N time retrival
    const metricsMap = Object.entries(service.metrics).reduce(
      (accum, next) => {
        const metrics = next[1];
        const mappedMetrics = new Map(
          metrics.map(p => {
            const dateString = p.date.toString();
            dateSet.add(dateString);
            return [dateString, p];
          })
        );
        const unit = next[0];
        accum[unit] = mappedMetrics;
        return accum;
      },
      {} as Record<string, Map<string, MiniMetrics>>
    );

    if (accum[service.kind] instanceof Array) {
      service["metricsMap"] = metricsMap;
      accum[service.kind].push(service);
      return accum;
    }
    service["metricsMap"] = metricsMap;
    accum[service.kind] = [service];
    return accum;
  }, {} as GroupType);
  const dates = Array.from(dateSet).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );
  return { servicekind, dates };
};

export const groupByServiceKind = (
  serviceMetrics: ServiceMetricsType[],
  filter: TotalFilterType
) => {
  const { servicekind: skind, dates } = groupByKindName(serviceMetrics);
  const kinds = ["Applications", "Workers", "Services"] as const;
  const servicekind = {
    Applications: skind?.webapp,
    Workers: skind?.worker,
    Services: skind?.service
  };
  const resultInit = resouceUnit.reduce(
    (a, u) => {
      a[u] = [];
      return a;
    },
    {} as Record<MetricsUnit, SeriesDataType[]>
  );

  const result = dates.reduce((resultAccum, timestamp) => {
    const unitData = resouceUnit.reduce((accum, unit) => {
      const gInit = { data: [] as any, date: new Date(timestamp) };

      // collect metrics for each group of webapp, worker and services
      const groupData = kinds.reduce((accum, kind) => {
        const services = servicekind?.[kind];
        const serviceInit = { value: 0, info: [] as any, allocation: 0 };

        // sum metrics data for each services in the group
        const serviceData = services?.reduce((accums, service) => {
          const point = service?.metricsMap?.[unit]?.get?.(timestamp);
          const { value, max } = getInPointInstance(point, service.name, unit);
          accums.value = accums.value += value;
          accums.allocation = accums.allocation += max;
          accums.info.push({ ...point, service });
          return accums;
        }, serviceInit);
        //service kind point data after accumulation
        filter?.isUsage
          ? (accum[kind] = serviceData?.value || 0)
          : (accum[kind] = serviceData?.allocation || 0);
        accum.data = accum.data.concat(serviceData?.info);
        return accum;
      }, gInit);

      accum[unit] ? accum[unit].push(groupData) : (accum[unit] = [groupData]);
      return accum;
    }, {});

    resultAccum["/mnt"].push(unitData["/mnt"][0]);
    resultAccum["/tmp"].push(unitData["/tmp"][0]);
    resultAccum["cpu"].push(unitData["cpu"][0]);
    resultAccum["memory"].push(unitData["memory"][0]);
    return resultAccum;
  }, resultInit);
  return result;
};

export const groupByServiceName = (
  serviceMetrics: ServiceMetricsType[],
  filter: TotalFilterType
) => {
  const { dates } = groupByKindName(serviceMetrics);

  const seriesInit = resouceUnit.reduce(
    (a, u) => {
      a[u] = [];
      return a;
    },
    {} as Record<MetricsUnit, SeriesDataType[]>
  );

  const series = dates.reduce((seriesAccum, date) => {
    const unitData = resouceUnit.reduce((accum, unit) => {
      // collect metrics for each service
      type P = { point?: MiniMetrics; name: string };
      const init = { data: [] as unknown as P[], date: new Date(date) };
      const groupData = serviceMetrics.reduce((accum, service) => {
        const point = service?.metricsMap?.[unit]?.get?.(date);
        const { value, max } = getInPointInstance(point, service.name, unit);
        if (filter?.isUsage) {
          accum[service.name] = value || 0;
        } else {
          accum[service.name] = max || 0;
        }
        accum.data.push({ point, name: service.name });
        return accum;
      }, init);
      accum[unit] ? accum[unit].push(groupData) : (accum[unit] = [groupData]);
      return accum;
    }, {});

    seriesAccum["/mnt"].push(unitData["/mnt"][0]);
    seriesAccum["/tmp"].push(unitData["/tmp"][0]);
    seriesAccum["cpu"].push(unitData["cpu"][0]);
    seriesAccum["memory"].push(unitData["memory"][0]);
    return seriesAccum;
  }, seriesInit);

  return series;
};

export const getStackKeys = (
  serviceMetrics: ServiceMetricsType[],
  filter: TotalFilterType
) => {
  const names = filter.isKindGroup
    ? ["Applications", "Workers", "Services"]
    : serviceMetrics?.map(s => s.name) || [];

  const bands = {};
  const numColors = colorBands.length;
  names.forEach((name, index) => {
    const colorIndex = index % numColors;
    bands[name] = colorBands[colorIndex];
  });

  return { names, bands };
};

export const getStackKeys2 = (
  serviceList: string[],
  filter: TotalFilterType
) => {
  const names = filter.isKindGroup
    ? ["webapp", "worker", "service"]
    : serviceList || [];

  const bands = {};
  const numColors = colorBands.length;
  names.forEach((name, index) => {
    const colorIndex = index % numColors;
    bands[name] = colorBands[colorIndex];
  });

  return { names, bands };
};

export const calculateUsage = (serviceMetrics?: ServiceMetricsType[]) => {
  const allocInit = {
    cpu: { usage: 0, allocation: 0 },
    "/mnt": { usage: 0, allocation: 0 },
    "/tmp": { usage: 0, allocation: 0 },
    memory: { usage: 0, allocation: 0 }
  };
  return serviceMetrics?.reduce((unitsAllocs, d) => {
    resouceUnit.forEach(k => {
      const unitMetrics = d.metrics[k] || [];
      const p = unitMetrics[unitMetrics.length - 1];
      const { max, value } = getInPointInstance(p, d.name, k);

      const unitUsage = value;
      const unitAlloc = max;

      const { usage, allocation } = unitsAllocs[k];
      unitsAllocs[k] = {
        usage: unitUsage + usage,
        allocation: unitAlloc + allocation
      };
    });
    return unitsAllocs;
  }, allocInit);
};

export const calculateUsage2 = (
  serviceMetrics: Record<string, MetricsUnit2> = {}
) => {
  const allocInit = {
    cpu: { usage: 0, allocation: 0 },
    "/mnt": { usage: 0, allocation: 0 },
    "/tmp": { usage: 0, allocation: 0 },
    memory: { usage: 0, allocation: 0 }
  };
  return Object.entries(serviceMetrics)?.reduce((alloc, [unit, data]) => {
    const unitUsage = Math.max(...data.map(({ sum }) => sum.value));
    const unitAlloc = Math.max(...data.map(({ sum }) => sum.max));
    alloc[unit] = {
      usage: unitUsage,
      allocation: unitAlloc
    };
    return alloc;
  }, allocInit);
};
